/**
 * ForecastScratchpad — quick-261001-nym "Exec Scratchpad".
 *
 * John, 2026-10-01: a prompt at the bottom of the tab where an exec types a
 * scenario — "if we only close trustmark, alight, and the unum managed services,
 * and then like 20% of others, what is the total?" — and gets the arithmetic
 * back. Round 2 ("lets make it more sophisticated"): the sentence is read by
 * Claude through POST /api/forecast/scratchpad, which returns WHICH deals close,
 * the rate for the rest and its assumptions; the arithmetic is done here with
 * the shared `applyScenario`, so the working is always shown. The rules reading
 * (`readScenario`) answers instantly and stays as the fallback when the server
 * has no Claude key (503) or the call fails.
 */
import {
  applyScenario,
  briefDeals,
  parseScenarioReading,
  readScenario,
  type ScratchpadResult,
} from '@saledash/shared/forecast/scratchpad';
import { useEffect, useRef, useState } from 'react';

import { formatAmount } from '../../lib/format.js';
import { Card } from '../ui/Card.js';

import type { ForecastDeal } from '@saledash/shared';

interface ForecastScratchpadProps {
  /** The quarter on screen's deals (Product + Managed Services). */
  deals: readonly ForecastDeal[];
  quarterTitle: string;
}

const EXAMPLE = 'if we only close trustmark, alight, and the unum managed services, and then like 20% of others, what is the total?';

type Source = 'rules' | 'claude' | 'claude-pending' | 'claude-unavailable';

export function ForecastScratchpad({ deals, quarterTitle }: ForecastScratchpadProps): React.JSX.Element {
  const [text, setText] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const [result, setResult] = useState<ScratchpadResult | null>(null);
  const [source, setSource] = useState<Source>('rules');
  const seq = useRef(0);

  useEffect(() => {
    if (asked === null) return;
    const mine = ++seq.current;
    // 1. Rules reading, instantly.
    setResult(readScenario(asked, deals));
    setSource('claude-pending');
    // 2. Claude's reading, when the server is configured for it.
    const ctrl = new AbortController();
    void (async () => {
      try {
        const res = await fetch('/api/forecast/scratchpad', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ text: asked, deals: briefDeals(deals) }),
          signal: ctrl.signal,
        });
        if (mine !== seq.current) return;
        if (!res.ok) { setSource(res.status === 503 ? 'claude-unavailable' : 'rules'); return; }
        const body = (await res.json()) as { reading?: unknown };
        const reading = parseScenarioReading(body.reading ?? null);
        if (!reading || mine !== seq.current) { setSource('rules'); return; }
        setResult(applyScenario(deals, reading));
        setSource('claude');
      } catch {
        if (mine === seq.current) setSource('rules');
      }
    })();
    return () => { ctrl.abort(); };
  }, [asked, deals]);

  const sourceLabel: Record<Source, string> = {
    rules: 'Read by the rules (name match, % for the rest)',
    'claude-pending': 'Rules reading shown — asking Claude…',
    claude: "Claude's reading — arithmetic done here",
    'claude-unavailable': 'Read by the rules — Claude reading not configured on this server',
  };

  return (
    <Card data-testid="forecast-scratchpad">
      <p className="text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">Exec Scratchpad</p>
      <p className="mt-1 text-xs text-ink-500">
        Describe a {quarterTitle} scenario in plain words. Name the deals you expect to close and a percentage for
        the rest; the total comes back with the working shown.
      </p>
      <form
        className="mt-3 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const t = text.trim() || EXAMPLE;
          if (!text.trim()) setText(EXAMPLE);
          setAsked(t);
        }}
      >
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); }}
          placeholder={EXAMPLE}
          aria-label="Scenario"
          data-testid="forecast-scratchpad-input"
          className="min-w-0 flex-1 rounded-md border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none"
        />
        <button
          type="submit"
          data-testid="forecast-scratchpad-run"
          className="rounded-md border border-brand-500 bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:brightness-105"
        >
          Work it out
        </button>
      </form>
      {result && (
        <div className="mt-3 space-y-1 text-sm" data-testid="forecast-scratchpad-result">
          <p className="text-[0.7rem] uppercase tracking-wider text-ink-400" data-testid="forecast-scratchpad-source">
            {sourceLabel[source]}
          </p>
          {result.empty && (
            <p className="text-ink-500">
              Nothing in that matched a deal name or gave a percentage. Try the account as it appears in the tables,
              e.g. “Trustmark”, or add “20% of others”.
            </p>
          )}
          {result.lines.map((l, i) => (
            <p key={i} className={l.kind === 'note' ? 'text-xs text-ink-500' : 'text-ink-700'} data-testid={`forecast-scratchpad-line-${l.kind}`}>
              {l.text}
            </p>
          ))}
          <p className="pt-1 text-base font-bold tabular-nums text-ink-900" data-testid="forecast-scratchpad-total">
            Total {formatAmount(result.total)}
          </p>
        </div>
      )}
    </Card>
  );
}
